<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('course.general_title') }}
        </h6>

        <CreateButton
            :label="$t('course.group_coaching.create')"
            :to="{ name: 'courseGroupCoachingForm' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('course.group_coaching.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            :clientDropdown="clientDropdown"
            :operationTypeDropdown="operationTypeDropdown"
            :courseNameDropdown="courseNameDropdown"
            :courseActionNumberDropdown="courseActionNumberDropdown"
            @search="getCourses"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCourses"/>

        <EmptyState
            v-if="!courses.data?.length"
            icon="fa-clipboard-list"
            title="Keine Maßnahmen gefunden"
            subtitle="Es wurden keine Maßnahmen gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>{{ $t('general.designation') }}</th>
              <th>{{ $t('general.operation_type') }}</th>
              <th>{{ $t('general.action_number') }}</th>
              <th>{{ $t('general.validity_start') }}</th>
              <th>{{ $t('general.validity_end') }}</th>
              <th>{{ $t('general.location') }}</th>
              <th>{{ $t('general.payer') }}</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(course) in courses.data" :key="course.id">
              <td>{{ course?.nameOfCoaching }}</td>
              <td>{{ course?.operationTypeTranslated }}</td>
              <td>{{ course?.actionNumber }}</td>
              <td>{{ course?.startDate }}</td>
              <td>{{ course?.endDate }}</td>
              <td>{{ course?.location.name }}</td>
              <td>
                <span v-for="(client, index) in course.clients" :key="client.id">
                    {{ client.name }} <span v-if="index < course.clients.length - 1">, </span>&nbsp;
                </span>
              </td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click="router.push({name: 'courseGroupCoachingForm', params: {id: course?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click="deleteModal(course?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="courses?.pagination?.total_pages > 1"
            :totalPages="courses?.pagination?.total_pages"
            :currentPage="courses?.pagination?.current_page"
            :totalItems="courses?.pagination?.total_items"
            :perPage="courses?.pagination?.items_per_page"
            @pagechanged="getCourses"/>
      </div>
    </div>

    <ModalDelete
        @update:modelValue="updateModal"
        :modelValue="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteCourse">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup>
import courseGroupCoachingService from '@/api/Course/GroupCoaching';
import dropdownService from '@/api/Dropdown';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { getClientDropdown, getOfficeDropdown } from '@/shared/dropdowns';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import Search from '@/views/App/Course/GroupCoaching/Search.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const piniaEnumsState = piniaEnumsStore();
const getEnums        = computed(() => piniaEnumsState.enums);
const { t }           = useI18n();
const courses         = ref([]);
const filters         = ref({
  courseId: '',
  actionNumber: '',
  officeId: '',
  clientId: '',
  operationType: '',
});

const showModal                  = ref(false);
const courseId                   = ref(0);
const isSearching                = ref(false);
const isLoaded                   = ref(false);
const operationTypeDropdown      = ref([]);
const officeDropdown             = ref([]);
const clientDropdown             = ref([]);
const courseNameDropdown         = ref([]);
const courseActionNumberDropdown = ref([]);

async function getCourses(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = courses.value?.pagination?.items_per_page;

  try {
    const { data } = await courseGroupCoachingService.getCourses({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    courses.value     = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteCourse() {
  try {
    await courseGroupCoachingService.deleteCourse(courseId.value);

    filters.value.courseId      = '';
    filters.value.officeId      = '';
    filters.value.clientId      = '';
    filters.value.operationType = '';
    filters.value.actionNumber  = '';
    showModal.value             = false;

    await getCourses();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getCourseNameAndActionNumberDropdown() {
  try {
    const { data }           = await dropdownService.getCourseGroupCoaching();
    courseNameDropdown.value = data.data.map(item => ({
      label: `${item.nameOfCoaching} `,
      value: item?.id,
    }));

    courseActionNumberDropdown.value = data.data
        .filter(item => !!item.actionNumber)
        .map(item => ({
          label: `${item.actionNumber} `,
          value: item?.actionNumber,
        }));
  } catch (error) {
    await ErrorToast(error);
  }
}

function updateModal(value) {
  CloseToast();
  showModal.value = value;
}

function deleteModal(id) {
  CloseToast();
  courseId.value  = id;
  showModal.value = true;
}

async function getOperationTypeDropdown() {
  operationTypeDropdown.value = getEnums?.value?.groupCoachingOperationTypes || []
}

onMounted(async () => {
  //if (Object.keys(getEnums.value).length === 0) {
  //  await store.dispatch('getEnums', { t });
  //}

  await Promise.all([
    getOperationTypeDropdown(),
    getOfficeDropdown(officeDropdown),
    getClientDropdown(clientDropdown),
    getCourseNameAndActionNumberDropdown(),
    getCourses(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>